import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../repositories/admin";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

interface Label {
  id: number;
  name: string;
}

export default function InviteUserPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [labels, setLabels] = useState<Label[]>([]); // Updated type to Label[]
  const [selectedLabel, setSelectedLabel] = useState(""); // Selected label
  const navigate = useNavigate();

  // Fetch labels from the backend
  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const response = await adminApi.getLabels();
        setLabels(response.data.data || []); // Ensure labels are set correctly
      } catch (error) {
        console.error("Error fetching labels:", error);
        setMessage("❌ Failed to fetch labels. Please try again.");
      }
    };

    fetchLabels();
  }, []);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleInvite = async () => {
    if (!email) {
      setMessage("⚠️ Please enter a valid email.");
      return;
    }

    if (!isValidEmail(email)) {
      setMessage("⚠️ Invalid email format. Please enter a valid email.");
      return;
    }

    if (!selectedLabel) {
      setMessage("⚠️ Please select a label.");
      return;
    }

    try {
      setLoading(true);
      setMessage("");
      await adminApi.inviteUser({ email, label: selectedLabel }); // Send email and selected label
      setMessage("✅ Invite sent successfully!");
      setEmail("");
      setSelectedLabel("");
    } catch (error) {
      console.error("Error sending invite:", error);
      setMessage("❌ Failed to send invite. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-6 rounded-lg shadow-lg bg-gray-50">
      {/* Card Container */}
      <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-lg">
        {/* Back Button */}
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center mb-4 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" /> Back
        </button>

        <h2 className="mb-4 text-2xl font-bold text-center text-gray-800">
          Invite a User
        </h2>
        <p className="mb-6 text-center text-gray-500">
          Select a label and enter an email address to send an invite.
        </p>

        {/* Label Dropdown */}
        <div className="relative mb-4">
          <select
            className="w-full p-3 transition border rounded-lg shadow-sm focus:outline-none focus:ring focus:border-blue-400"
            value={selectedLabel}
            onChange={(e) => setSelectedLabel(e.target.value)}
          >
            <option value="" disabled>
              Select a label
            </option>
            {labels.map((label) => (
              <option key={label.id} value={label.name}>
                {label.name}
              </option>
            ))}
          </select>
        </div>

        {/* Email Input */}
        <div className="relative mb-4">
          <input
            type="email"
            className="w-full p-3 transition border rounded-lg shadow-sm focus:outline-none focus:ring focus:border-blue-400"
            placeholder="Enter user email..."
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {/* Invite Button */}
        <button
          className={`w-full mt-4 py-3 text-white font-semibold rounded-lg transition ${
            loading
              ? "bg-blue-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
          onClick={handleInvite}
          disabled={loading}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="w-5 h-5 mr-2 animate-spin" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="white"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="white"
                  d="M4 12a8 8 0 0116 0 8.001 8.001 0 01-16 0z"
                ></path>
              </svg>
              Sending...
            </span>
          ) : (
            "Send Invite"
          )}
        </button>

        {/* Response Message */}
        {message && (
          <p className="mt-4 font-medium text-center text-gray-700">{message}</p>
        )}
      </div>
    </div>
  );
}